import React from "react";
import { ensureProductionThemeApplied, attachThemeRefreshListener } from "@/theme/runtime";

import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import Index from "./pages/Index";
import NotFound from "./pages/NotFound";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import TermsOfService from "./pages/TermsOfService";
import ThemePreview from "@/pages/theme-preview"; // Theme testbed
import { Recognitions } from "@/components/Recognitions"; // TEMP debug
import ImagePrepFCA from "@/pages/ImagePrepFCA";
import EdibleArtControl from "@/pages/EdibleArtControl";
import SeoMaintenance from "@/pages/seo/SeoMaintenance";
import ImageManagement from "@/pages/image-management"; 
import { Navigate } from "react-router-dom";


const queryClient = new QueryClient();

function ProtectedRoute({ children }: { children: JSX.Element }) {
  const authed =
    typeof window !== "undefined" &&
    localStorage.getItem("fca_admin_authed_v1") === "1";
  return authed ? children : <Navigate to="/EdibleArtControl" replace />;
}

const App: React.FC = () => {
  // ⬇️ Apply latest Production theme once at app start and attach a refresh listener.
  React.useEffect(() => {
    let detach: (() => void) | undefined;
    (async () => {
      await ensureProductionThemeApplied();    // server → fallback to file-based production
      detach = attachThemeRefreshListener();   // optional: responds to window "theme:refresh"
    })();
    return () => { detach?.(); };
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <Routes>
            <Route path="/" element={<Index />} />
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            <Route path="/terms-of-service" element={<TermsOfService />} />

            <Route
              path="/recognitions-debug"
              element={
                <ProtectedRoute>
                  <Recognitions />
                </ProtectedRoute>
              }
            />
            <Route
              path="/theme-preview"
              element={
                <ProtectedRoute>
                  <ThemePreview />
                </ProtectedRoute>
              }
            />
            <Route
              path="/image-prep-fca"
              element={
                <ProtectedRoute>
                  <ImagePrepFCA />
                </ProtectedRoute>
              }
            />

            {/* ⬇️ ADMIN PAGE (HIDDEN/UNLINKED) */}
            <Route // <-- THIS ROUTE WAS MISSING
              path="/image-management"
              element={
                <ProtectedRoute>
                  <ImageManagement />
                </ProtectedRoute>
              }
            />
            
            {/* SEO Maintenance mirrors ThemePreview behavior */}
            <Route
              path="/seo-maintenance"
              element={
                <ProtectedRoute>
                  <SeoMaintenance />
                </ProtectedRoute>
              }
            />
            
            <Route path="/EdibleArtControl" element={<EdibleArtControl />} />
            
            {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
            <Route path="*" element={<NotFound />} />
          </Routes>
        </BrowserRouter>
      </TooltipProvider>
    </QueryClientProvider>
  );
};

export default App;
